/* eslint-disable no-unused-vars */
import {
  ArrowLeftOutlined,
  DownOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  PlayCircleOutlined,
  ClockCircleOutlined,
  UserOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Checkbox,
  Dropdown,
  Form,
  notification,
  Row,
  Skeleton,
  Typography,
  Space,
  Tag,
  Divider,
  Statistic,
  Col,
} from 'antd';
import { useForm } from 'antd/es/form/Form';
import OrderItemCard from 'components/applications/OrderItemCard';
import OrderItemList from 'components/lists/OrderItemList';
import { useCallGetApi, usePutApi } from 'hooks/useApi';
import { useGetToApi } from 'hooks/useApi/useGetAxios';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import paths from 'assets/routes/paths';
import { dateFormatter } from 'utils/formaters';

const { Title, Text } = Typography;

const items = [
  {
    key: 0,
    label: 'Preestablecer',
    icon: <ClockCircleOutlined />,
    color: 'default',
  },
  {
    key: 1,
    label: 'Marcar Inicio Labores',
    icon: <PlayCircleOutlined />,
    color: 'processing',
  },
  {
    key: 3,
    label: 'Marcar Finalización',
    icon: <CheckCircleOutlined />,
    color: 'success',
  },
];

const OrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [orderData, isLoading, callOrder] = useGetToApi(`/order/name/items?id_order=${id}`, {
    initialValues: {
      order: {
        items: [],
        name_client: 'La orden no existe',
      },
    },
  });
  const order = useMemo(() => {
    return orderData.order;
  }, [orderData.order]);

  // Calcular estadísticas de items (excluyendo ocultos)
  const itemStats = useMemo(() => {
    if (!order?.items)
      return {
        total: 0,
        preestablecidos: 0,
        enProceso: 0,
        finalizados: 0,
      };

    // Filtrar items visibles (excluir status_id = 10)
    const visibleItems = order.items.filter((item) => item.status_id !== 10);

    const total = visibleItems.length;
    const preestablecidos = visibleItems.filter((item) => item.item_status_id === 0).length;
    const enProceso = visibleItems.filter((item) => item.item_status_id === 1).length;
    const finalizados = visibleItems.filter((item) => item.item_status_id === 3).length;

    return { total, preestablecidos, enProceso, finalizados };
  }, [order?.items]);

  const [form] = useForm();

  useEffect(() => {
    callOrder();
  }, [callOrder]);
  const onFinishOrder = () => {
    // status 10 = oculto/eliminado. No bloquea el cierre: el panel de
    // estadisticas ya los excluye via visibleItems, el check debe hacer lo mismo.
    const itemsNotFinished = (order.items || []).filter(
      (item) => item.item_status_id !== 3 && item.item_status_id !== 10
    );
    if (itemsNotFinished.length !== 0) {
      notification.warning({
        message: 'Es necesario finalizar todos los items para poder Entregar la Orden de trabajo',
      });
    } else {
      return navigate(paths.relative.specialist.orderFinishForm(id));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        <Skeleton loading={isLoading}>
          {/* Header */}
          <Card className="shadow-sm border-0 bg-gradient-to-r from-blue-50 to-blue-100">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
              <div className="flex items-center space-x-3">
                <Link to={paths.relative.specialist.order}>
                  <Button icon={<ArrowLeftOutlined />} className="rounded-lg" type="primary" />
                </Link>
                <div className="bg-blue-600 p-2 rounded-lg">
                  <FileTextOutlined className="text-white text-lg" />
                </div>
                <div>
                  <Title level={3} className="mb-0 text-blue-800">
                    {order.name_client}
                  </Title>
                  <Text type="secondary" className="text-blue-600">
                    Detalle de la orden de trabajo
                  </Text>
                </div>
              </div>
              <Space>
                <Button
                  type="primary"
                  onClick={onFinishOrder}
                  className="bg-green-600 hover:bg-green-700 border-green-600 rounded-lg"
                  icon={<CheckCircleOutlined />}
                >
                  Finalizar Orden
                </Button>
              </Space>
            </div>
          </Card>

          {/* Estadísticas de Items */}
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={6}>
              <Card className="shadow-sm border-0 text-center">
                <Statistic
                  title="Total Items"
                  value={itemStats.total}
                  prefix={<ToolOutlined className="text-blue-500" />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card className="shadow-sm border-0 text-center">
                <Statistic
                  title="Preestablecidos"
                  value={itemStats.preestablecidos}
                  prefix={<ClockCircleOutlined className="text-gray-500" />}
                  valueStyle={{ color: '#8c8c8c' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card className="shadow-sm border-0 text-center">
                <Statistic
                  title="En Proceso"
                  value={itemStats.enProceso}
                  prefix={<PlayCircleOutlined className="text-orange-500" />}
                  valueStyle={{ color: '#fa8c16' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card className="shadow-sm border-0 text-center">
                <Statistic
                  title="Finalizados"
                  value={itemStats.finalizados}
                  prefix={<CheckCircleOutlined className="text-green-500" />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Panel de Acciones */}

          {/* Lista de Items */}
          <Card
            className="shadow-sm border-0"
            title={
              <span className="flex items-center space-x-2">
                <FileTextOutlined className="text-green-600" />
                <span>Items de la Orden</span>
              </span>
            }
          >
            <Form form={form}>
              <OrderItemList
                items={order.items}
                isLoading={false}
                orderId={id}
                onItemUpdate={callOrder}
              />
            </Form>
          </Card>
        </Skeleton>
      </div>
    </div>
  );
};

export default OrderDetail;
